export interface JurisdictionFieldConfig {
  labelKey: string;
  label: string; // English fallback
  regex?: RegExp;
  format?: string;
  required?: boolean;
}

export interface JurisdictionIdentityConfig {
  registrationNumber: JurisdictionFieldConfig;
  taxEntityNumber?: JurisdictionFieldConfig;
  vatNumber: JurisdictionFieldConfig;
  taxExempt?: {
    labelKey: string;
    label: string;
  };
}

export const JURISDICTION_IDENTITY_CONFIG: Record<string, JurisdictionIdentityConfig> = {
  NL: {
    registrationNumber: {
      labelKey: 'businessIdentity.nl.kvk',
      label: 'KvK Number',
      regex: /^\d{8}$/,
      format: '8 digits',
      required: true,
    },
    taxEntityNumber: {
      labelKey: 'businessIdentity.nl.rsin',
      label: 'RSIN',
      regex: /^\d{9}$/,
      format: '9 digits',
    },
    vatNumber: {
      labelKey: 'businessIdentity.nl.btw',
      label: 'BTW Number',
      regex: /^NL\d{9}B\d{2}$/,
      format: 'NL + 9 digits + B + 2 digits',
    },
    taxExempt: {
      labelKey: 'businessIdentity.nl.anbi',
      label: 'ANBI Status',
    },
  },

  BE: {
    registrationNumber: {
      labelKey: 'businessIdentity.be.bce',
      label: 'BCE/KBO Number',
      regex: /^\d{10}$/,
      format: '10 digits',
      required: true,
    },
    vatNumber: {
      labelKey: 'businessIdentity.be.btw',
      label: 'BTW Number',
      regex: /^BE\d{10}$/,
      format: 'BE + 10 digits',
    },
  },

  DE: {
    registrationNumber: {
      labelKey: 'businessIdentity.de.handelsregister',
      label: 'Handelsregister',
      format: 'HRB + number',
    },
    vatNumber: {
      labelKey: 'businessIdentity.de.ust',
      label: 'USt-IdNr',
      regex: /^DE\d{9}$/,
      format: 'DE + 9 digits',
    },
  },

  FR: {
    registrationNumber: {
      labelKey: 'businessIdentity.fr.siren',
      label: 'SIREN',
      regex: /^\d{9}$/,
      format: '9 digits',
    },
    vatNumber: {
      labelKey: 'businessIdentity.fr.tva',
      label: 'TVA Number',
      regex: /^FR[A-Z0-9]{2}\d{9}$/,
      format: 'FR + 2 chars + 9 digits',
    },
  },

  GB: {
    registrationNumber: {
      labelKey: 'businessIdentity.gb.companyNumber',
      label: 'Company Number',
      regex: /^[A-Z0-9]{8}$/,
      format: '8 alphanumeric',
    },
    vatNumber: {
      labelKey: 'businessIdentity.gb.vat',
      label: 'VAT Number',
      regex: /^GB\d{9}$/,
      format: 'GB + 9 digits',
    },
    taxExempt: {
      labelKey: 'businessIdentity.gb.charity',
      label: 'Charity Status',
    },
  },

  US: {
    registrationNumber: {
      labelKey: 'businessIdentity.us.stateReg',
      label: 'State Registration',
      format: 'Varies by state',
    },
    taxEntityNumber: {
      labelKey: 'businessIdentity.us.ein',
      label: 'EIN',
      regex: /^\d{2}-\d{7}$/,
      format: 'XX-XXXXXXX',
    },
    vatNumber: {
      labelKey: 'businessIdentity.us.salesTax',
      label: 'Sales Tax ID',
      format: 'Varies by state',
    },
    taxExempt: {
      labelKey: 'businessIdentity.us.501c3',
      label: '501(c)(3) Status',
    },
  },

  DEFAULT: {
    registrationNumber: {
      labelKey: 'businessIdentity.default.registrationNumber',
      label: 'Registration Number',
    },
    vatNumber: {
      labelKey: 'businessIdentity.default.vatNumber',
      label: 'VAT Number',
    },
  },
};

export const getJurisdictionIdentityConfig = (countryCode: string | null | undefined): JurisdictionIdentityConfig => {
  return JURISDICTION_IDENTITY_CONFIG[countryCode || ''] || JURISDICTION_IDENTITY_CONFIG.DEFAULT;
};
